// Voicing-family construction and playable-fingering search.

import type { ChordTypeId } from './chords';
import {
  chordOwnsFlatNine,
  chordSymbol,
  chordToneName,
  chordToneNames,
  chordTonePitchClasses,
  getChordType,
} from './chords';
import { spellChordTones, spellVoicingMidis } from './chordSpelling';
import { enumerateJoePassVoicings } from './joePassVoicings';
import { pitchClassOf, sharpName, type PitchClass } from './notes';
import {
  STRING_LABELS,
  STRING_LAYOUTS,
  activeStrings,
  activeStringsForFingering,
  frettedMidi,
  noteAtFret,
  pitchClassAtFret,
  stringLayoutFor,
  type StringLayoutId,
  type StringSet,
  topStringForFingering,
} from './tuning';
import { createFamilyVoicing, type VoicingFamily } from './voicingFamilies';

export type Inversion = 0 | 1 | 2 | 3;

/** A concrete fingering: frets per 6 strings (null = unplayed). */
export interface Fingering {
  frets: (number | null)[];
  /** Voiced note names (sharp spelling), low to high across the active strings. */
  voicing: string[];
  /** The family determines the octave displacement applied to the close stack. */
  voicingFamily: VoicingFamily;
  /** Concrete physical strings; canonical for rendering, audio and analysis. */
  stringLayout: StringLayoutId;
  /** Lower/upper register preference retained for settings compatibility. */
  stringSet: StringSet;
  /** Source catalogue diagram, when a grip comes from a transcribed collection. */
  sourceShapeId?: string;
  /** Saved shape identity; construction is present only when it is known. */
  alternativeShape?: { id: string; name: string; construction?: 'drop-2' | 'drop-3' };
  /** A timed event with no compatible catalogue grip. All frets are silent;
   * its chord identity remains available to the bass, score and transport. */
  unavailable?: 'harmony' | 'melody';
}

/** A fully-described chord voicing for rendering and analysis. */
export interface ChordVoicing extends Fingering {
  /** Engine-internal root (sharp spelling). */
  root: string;
  /** Root as the caller spelled it (may be a flat). */
  displayRoot: string;
  chordType: ChordTypeId;
  inversion: Inversion;
  /** Display symbol using the display root, e.g. "Ebmaj7". */
  symbol: string;
}

/**
 * Build a drop-2 voicing from close-position chord tones.
 * Rotate the close voicing `inversion` times, then drop the second-from-top
 * note an octave to the bottom.
 */
export function createDrop2Voicing(notes: string[], inversion: Inversion): string[] {
  return createFamilyVoicing(notes, inversion, 'drop-2');
}

/** Build a Drop 3 voicing by lowering the third voice from the top. */
export function createDrop3Voicing(notes: string[], inversion: Inversion): string[] {
  return createFamilyVoicing(notes, inversion, 'drop-3');
}

/**
 * Search frets 3..12 for a playable fingering of the given drop-2 voicing on the
 * chosen string set, within `maxSpan` frets. Returns null if none is found.
 */
export function findDrop2Fingering(
  voicing: string[],
  startFret = 3,
  maxSpan = 4,
  stringSet: StringSet = 'middle',
): Fingering | null {
  return findVoicingFingering(voicing, startFret, maxSpan, stringSet, 'drop-2');
}

/** Search a family's concrete string layout for a playable realization. */
export function findVoicingFingering(
  voicing: string[],
  startFret = 3,
  maxSpan = 4,
  stringSet: StringSet = 'middle',
  voicingFamily: VoicingFamily = 'drop-2',
): Fingering | null {
  if (voicingFamily === 'joe-pass') return null;
  const layout = stringLayoutFor(voicingFamily, stringSet);
  const strings = activeStrings(stringSet, voicingFamily);
  if (voicing.length !== strings.length) return null;

  // Match by PITCH CLASS, not by name string: voicing names are now spelled
  // diatonically (Gb, Bbb) while noteAtFret speaks the engine's sharps, and a
  // string comparison would silently find no fingering for any flat spelling.
  const voicingPcs = voicing.map(pitchClassOf);
  for (let baseFret = startFret; baseFret <= 12; baseFret++) {
    const options = voicingPcs.map((pc, index) => {
      const found: number[] = [];
      const stringIdx = strings[index];
      for (let fret = Math.max(0, baseFret - 2); fret <= baseFret + maxSpan; fret++) {
        if (pitchClassAtFret(stringIdx, fret) === pc) found.push(fret);
      }
      return found;
    });
    if (options.some((frets) => frets.length === 0)) continue;

    // Pitch classes alone are not enough on guitar: the B/E tuning break and
    // skipped strings can put a nominal "top" voice below an inner voice. Walk
    // the tiny Cartesian product and require the sounding MIDI pitches to rise
    // strictly from the bass string to the melody string.
    const chosen: number[] = [];
    const choose = (voice: number, previousMidi: number): number[] | null => {
      if (voice === options.length) {
        const used = chosen.filter((fret) => fret > 0);
        return used.length > 0 && Math.max(...used) - Math.min(...used) <= maxSpan
          ? [...chosen]
          : null;
      }
      for (const fret of options[voice]) {
        const midi = frettedMidi(strings[voice], fret);
        if (midi <= previousMidi) continue;
        chosen.push(fret);
        const result = choose(voice + 1, midi);
        if (result) return result;
        chosen.pop();
      }
      return null;
    };
    const selected = choose(0, Number.NEGATIVE_INFINITY);
    if (selected) {
      const frets: (number | null)[] = [null, null, null, null, null, null];
      strings.forEach((stringIndex, voice) => {
        frets[stringIndex] = selected[voice];
      });
      return {
        frets,
        voicing,
        stringSet,
        voicingFamily,
        stringLayout: layout.id,
      };
    }
  }

  return null;
}

/**
 * Detect a minor-NINTH ("b9") interval between any two voices — the classic
 * "avoid" interval, in its TRUE reading (ruling Aug 2026, closing the open
 * question this used to carry): a pair 13+ semitones apart reducing to a
 * semitone class. An adjacent minor SECOND deliberately passes — it is a
 * tight colour, not the clash the rule names, and flagging it rejected the
 * textbook 3rd-inversion maj7 drop-2 (E-B-C-G) while the genuinely harsh
 * root-over-7th spread (B-E-G-C, a true m9 bass to top) stays flagged. This
 * is the same predicate as the piano path's `hasFlatNineClashMidis`, so the
 * two instruments now agree on what the rule means.
 */
export function hasFlatNineAvoidInterval(fingering: Fingering, stringSet?: StringSet): boolean {
  void stringSet;
  const pitches = activeStringsForFingering(fingering)
    .map((stringIndex) => {
      const fret = fingering.frets[stringIndex];
      return fret === null || fret === undefined ? null : frettedMidi(stringIndex, fret);
    })
    .filter((p): p is number => p !== null);

  for (let lower = 0; lower < pitches.length; lower++) {
    for (let upper = 0; upper < pitches.length; upper++) {
      if (lower === upper) continue;
      const interval = pitches[upper] - pitches[lower];
      if (interval >= 13 && interval % 12 === 1) return true;
    }
  }
  return false;
}

/** Whether a voicing should be used given the global avoid-b9 setting. A
 * chord whose own formula carries a ♭9 (dom7♭9, m7♭5♭9) is exempt — its
 * root-to-♭9 minor ninth is the chord, not a clash (`chordOwnsFlatNine`,
 * shared with the piano path). */
export function shouldUseVoicing(
  fingering: Fingering,
  stringSet: StringSet,
  typeId: ChordTypeId,
  avoidB9: boolean,
): boolean {
  if (!avoidB9) return true;
  if (chordOwnsFlatNine(typeId)) return true;
  return !hasFlatNineAvoidInterval(fingering, stringSet);
}

/** Generate a full chord voicing, or null when no playable fingering exists.
 * Note names are spelled DIATONICALLY against `spellingRoot` (default: the
 * root as given) — an E♭7 grip reads Eb G Bb Db, never a C♯ — falling back to
 * the engine's sharp names only when the speller cannot answer. The physical
 * search is untouched: `pitchClassOf` reads any spelling, doubles included. */
export function generateChordVoicing(
  rootDisplay: string,
  typeId: ChordTypeId,
  inversion: Inversion,
  stringSet: StringSet,
  voicingFamily: VoicingFamily = 'drop-2',
  spellingRoot: string = rootDisplay,
): ChordVoicing | null {
  if (voicingFamily === 'joe-pass') {
    return (
      enumerateJoePassVoicings(rootDisplay, typeId, { displayRoot: spellingRoot }).find(
        (grip) => grip.inversion === inversion,
      ) ?? null
    );
  }
  return generateChordVoicingFromNotes(
    rootDisplay,
    typeId,
    spellChordTones(spellingRoot, typeId) ?? chordToneNames(rootDisplay, typeId),
    inversion,
    stringSet,
    voicingFamily,
  );
}

/** Generate a drop-2 grip from an explicit four-note realization while
 * retaining the source chord identity for symbols and interval analysis. */
export function generateChordVoicingFromNotes(
  rootDisplay: string,
  typeId: ChordTypeId,
  notes: string[],
  inversion: Inversion,
  stringSet: StringSet,
  voicingFamily: VoicingFamily = 'drop-2',
): ChordVoicing | null {
  // Melody-led callers may supply a four-note realization. It must never
  // manufacture a grip that is absent from the Joe Pass collection.
  if (voicingFamily === 'joe-pass') {
    return (
      enumerateJoePassVoicings(rootDisplay, typeId).find((grip) => grip.inversion === inversion) ??
      null
    );
  }
  const internalRoot = sharpName(pitchClassOf(rootDisplay));
  const voicing = createFamilyVoicing(notes, inversion, voicingFamily);
  const fingering =
    findVoicingFingering(voicing, 3, 4, stringSet, voicingFamily) ??
    // A few skipped-string Drop 3 shapes are only available with an open bass
    // or inner string. Keep the established fret-3-first result when it exists,
    // then admit that practical open-position fallback.
    (voicingFamily === 'drop-3'
      ? findVoicingFingering(voicing, 0, 4, stringSet, voicingFamily)
      : null);
  if (!fingering) return null;

  return {
    ...fingering,
    root: internalRoot,
    displayRoot: rootDisplay,
    chordType: typeId,
    inversion,
    symbol: chordSymbol(rootDisplay, typeId),
  };
}

/** All physical choices, rather than one representative per close-stack
 * rotation. Joe Pass uses its complete catalogue; existing drop families
 * preserve their established four-grip order and exact frets. */
export function enumerateChordVoicings(
  rootDisplay: string,
  typeId: ChordTypeId,
  stringSet: StringSet,
  voicingFamily: VoicingFamily = 'drop-2',
  spellingRoot = rootDisplay,
): ChordVoicing[] {
  if (voicingFamily === 'joe-pass') {
    return enumerateJoePassVoicings(rootDisplay, typeId, { displayRoot: spellingRoot });
  }
  return ([0, 1, 2, 3] as const).flatMap((inversion) => {
    const grip = generateChordVoicing(
      rootDisplay,
      typeId,
      inversion,
      stringSet,
      voicingFamily,
      spellingRoot,
    );
    return grip ? [grip] : [];
  });
}

/**
 * A grip the user PLACED (voicingPins.ts, the place-the-notes editor): frets on
 * one of the four string layouts, named against the chord. Every downstream
 * consumer — the distance metric, the top string, the readout rows — reads a
 * fingering through its layout, which is why a placed grip is confined to the
 * layouts the enumerators use rather than any six-string shape. Null when the
 * layout's top string is silent (the melody voice must exist) or fewer than
 * two strings sound. `inversion` is the rotation the bass tone would name in
 * the close stack, so the labels read like the enumerated grips'.
 */
export function gripFromFrets(
  rootDisplay: string,
  typeId: ChordTypeId,
  frets: readonly (number | null)[],
  stringLayout: StringLayoutId,
): ChordVoicing | null {
  const layout = STRING_LAYOUTS[stringLayout];
  const full: (number | null)[] = [null, null, null, null, null, null];
  let sounding = 0;
  for (const s of layout.strings) {
    const fret = frets[s];
    if (fret == null || fret < 0 || fret > 24 || !Number.isInteger(fret)) continue;
    full[s] = fret;
    sounding += 1;
  }
  const top = layout.strings[layout.strings.length - 1];
  if (sounding < 2 || (stringLayout !== 'joe-pass' && full[top] == null)) return null;
  const rootPc = pitchClassOf(rootDisplay);
  const strings = layout.strings.filter((s) => full[s] != null);
  const voicing = spellVoicingMidis(
    strings.map((s) => frettedMidi(s, full[s]!)),
    rootDisplay,
    typeId,
    sharpName,
  );
  const tones = chordTonePitchClasses(rootDisplay, typeId);
  const bassPc =
    stringLayout === 'joe-pass'
      ? Math.min(
          ...layout.strings.filter((s) => full[s] != null).map((s) => frettedMidi(s, full[s]!)),
        ) % 12
      : pitchClassOf(voicing[0]);
  const rotation = tones.indexOf(bassPc);
  return {
    frets: full,
    voicing,
    voicingFamily: layout.family,
    stringLayout,
    stringSet: stringLayout === 'joe-pass' && full[5] != null ? 'upper' : layout.register,
    root: sharpName(rootPc),
    displayRoot: rootDisplay,
    chordType: typeId,
    inversion: (rotation >= 0 && rotation <= 3 ? rotation : 0) as Inversion,
    symbol: chordSymbol(rootDisplay, typeId),
  };
}

/**
 * @deprecated These names describe the source close-stack rotation used by
 * the original Drop 2 UI, not the inversion of the finished open voicing.
 * Prefer `finishedInversionName` when a concrete fingering is available.
 */
export const INVERSION_NAMES = [
  'Root-position close',
  '1st-inversion close',
  '2nd-inversion close',
  '3rd-inversion close',
];

/** @deprecated Source-rotation label; use `finishedInversionName` for a grip. */
export function inversionName(inversion: number): string {
  return INVERSION_NAMES[inversion] ?? 'Root-position close';
}

/** @deprecated Bare ordinals hide the top and bass voices; use the identity helpers. */
export function voicingPositionName(position: number): string {
  return `Rotation ${Math.max(0, Math.min(3, Math.trunc(position))) + 1}`;
}

/** Note name on the top (melody) string of a fingering, or null. */
export function topNoteOf(fingering: Fingering, stringSet?: StringSet): string | null {
  void stringSet;
  const stringIndex = topStringForFingering(fingering);
  const fret = fingering.frets[stringIndex];
  if (fret === null || fret === undefined) return null;
  return noteAtFret(stringIndex, fret);
}

/** Note name on the lowest sounding string of a fingering, or null. */
export function bassNoteOf(fingering: Fingering): string | null {
  if (fingering.stringLayout === 'joe-pass') {
    const strings = activeStringsForFingering(fingering);
    if (!strings.length) return null;
    const bass = strings.reduce((low, string) =>
      frettedMidi(string, fingering.frets[string]!) < frettedMidi(low, fingering.frets[low]!)
        ? string
        : low,
    );
    return noteAtFret(bass, fingering.frets[bass]!);
  }
  const stringIndex = activeStringsForFingering(fingering).find((index) => {
    const fret = fingering.frets[index];
    return fret !== null && fret !== undefined;
  });
  if (stringIndex === undefined) return null;
  return noteAtFret(stringIndex, fingering.frets[stringIndex]!);
}

/**
 * Chord tone carried by the source close stack's top voice at a rotation.
 * This is family-neutral: Drop 2 and Drop 3 retain the same melody voice.
 */
export function topVoiceToneName(chordType: ChordTypeId, rotation: number): string {
  const tones = getChordType(chordType).formula.trim().split(/\s+/);
  const turn = Number.isFinite(rotation)
    ? ((Math.trunc(rotation) % tones.length) + tones.length) % tones.length
    : 0;
  return displayToneName(tones[(turn + tones.length - 1) % tones.length]);
}

export type FinishedInversionName =
  | 'Root position'
  | '1st inversion'
  | '2nd inversion'
  | '3rd inversion';

const FINISHED_INVERSION_NAMES: readonly FinishedInversionName[] = [
  'Root position',
  '1st inversion',
  '2nd inversion',
  '3rd inversion',
];

/** Map an actual finished inversion back to the source close-stack rotation.
 * This is defined for root-containing four-note chords; rootless extensions
 * deliberately have no conventional finished inversion. */
export function sourceRotationForFinishedInversion(
  family: VoicingFamily,
  finishedInversion: Inversion,
): Inversion {
  const rotations: Record<VoicingFamily, readonly Inversion[]> = {
    'drop-2': [2, 3, 0, 1],
    'drop-3': [3, 0, 1, 2],
    'joe-pass': [0, 1, 2, 3],
  };
  return rotations[family][finishedInversion];
}

function displayToneName(tone: string): string {
  return tone === '1' || tone === 'R' ? 'Root' : tone;
}

function toneNameOf(
  note: string | null,
  rootDisplay: string,
  chordType: ChordTypeId,
): string | null {
  if (!note) return null;
  const interval = (((pitchClassOf(note) - pitchClassOf(rootDisplay)) % 12) + 12) % 12;
  const type = getChordType(chordType);
  const toneIndex = type.intervals.findIndex(
    (chordInterval) => ((chordInterval % 12) + 12) % 12 === interval,
  );
  if (toneIndex < 0) return null;
  const tones = type.formula.trim().split(/\s+/);
  return displayToneName(tones[toneIndex]);
}

/**
 * Inversion of the finished, physically voiced grip. Rootless constructions
 * have no conventional inversion; any realization with the root in its bass does.
 */
export function finishedInversionName(
  fingering: Fingering,
  rootDisplay: string,
  chordType: ChordTypeId,
): FinishedInversionName | null {
  const bassNote = bassNoteOf(fingering);
  if (!bassNote) return null;
  const bassInterval = (((pitchClassOf(bassNote) - pitchClassOf(rootDisplay)) % 12) + 12) % 12;
  if (bassInterval === 0) {
    return 'Root position';
  }
  const type = getChordType(chordType);
  const rootIndex = type.intervals.findIndex((interval) => interval % 12 === 0);
  if (rootIndex < 0) return null;
  const bassToneIndex = type.intervals.findIndex(
    (interval) => ((interval % 12) + 12) % 12 === bassInterval,
  );
  return FINISHED_INVERSION_NAMES[bassToneIndex] ?? null;
}

/** Musical identity of a finished voicing, independent of its source rotation. */
export interface VoicingIdentity {
  topNote: string | null;
  topTone: string | null;
  bassNote: string | null;
  bassTone: string | null;
  finishedInversion: FinishedInversionName | null;
}

export function voicingIdentity(
  fingering: Fingering,
  rootDisplay: string,
  chordType?: ChordTypeId,
): VoicingIdentity {
  const topNote = topNoteOf(fingering);
  const bassNote = bassNoteOf(fingering);
  const rooted = bassNote != null && pitchClassOf(bassNote) === pitchClassOf(rootDisplay);
  return {
    topNote,
    topTone: chordType ? toneNameOf(topNote, rootDisplay, chordType) : null,
    bassNote,
    bassTone: rooted ? 'Root' : chordType ? toneNameOf(bassNote, rootDisplay, chordType) : null,
    finishedInversion: chordType ? finishedInversionName(fingering, rootDisplay, chordType) : null,
  };
}

/** Compact finished-voicing label, e.g. "2nd inversion · 7 on top". */
export function voicingIdentityLabel(
  fingering: Fingering,
  rootDisplay: string,
  chordType?: ChordTypeId,
): string {
  const identity = voicingIdentity(fingering, rootDisplay, chordType);
  const bassDescription =
    identity.finishedInversion ?? `${identity.bassTone ?? identity.bassNote ?? '—'} in bass`;
  const topDescription = identity.topTone ?? identity.topNote ?? '—';
  return `${bassDescription} · ${topDescription} on top`;
}

/** Fret span across the played (non-open) notes. */
export function fretSpan(fingering: Fingering, stringSet?: StringSet): number {
  void stringSet;
  const used = activeStringsForFingering(fingering)
    .map((s) => fingering.frets[s])
    .filter((f): f is number => f !== null && f !== undefined && f > 0);
  if (used.length === 0) return 0;
  return Math.max(...used) - Math.min(...used);
}

export interface VoiceRow {
  stringIndex: number;
  stringLabel: string;
  fret: number | null;
  note: string | null;
  interval: string | null;
}

/** Per-voice breakdown (string, fret, note, chord-tone) for an inspector view. */
export function voiceRows(
  fingering: Fingering,
  rootDisplay: string,
  stringSet?: StringSet,
  chordType?: ChordTypeId,
): VoiceRow[] {
  void stringSet;
  const rootPc: PitchClass = pitchClassOf(rootDisplay);
  return activeStringsForFingering(fingering).map((stringIndex) => {
    const fret = fingering.frets[stringIndex];
    const note = fret === null || fret === undefined ? null : noteAtFret(stringIndex, fret);
    return {
      stringIndex,
      stringLabel: STRING_LABELS[stringIndex],
      fret: fret ?? null,
      note,
      interval: note ? chordToneName(pitchClassOf(note), rootPc, chordType) : null,
    };
  });
}
